import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface DispositionHistoryTimelineProps {
  leadId: string;
  isVisible: boolean;
}

function getDotColor(subDisposition: string): string {
  if (subDisposition === 'Interested - Hot' || subDisposition === 'Interested - Warm') {
    return 'bg-green-500';
  }
  if (subDisposition === 'Callback Requested' || subDisposition === 'Follow Up Later') {
    return 'bg-orange-500';
  }
  if (subDisposition === 'Not Interested') {
    return 'bg-red-500';
  }
  if (subDisposition === 'No Answer' || subDisposition === 'Busy') {
    return 'bg-gray-400';
  }
  return 'bg-blue-500';
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

export const DispositionHistoryTimeline: React.FC<DispositionHistoryTimelineProps> = ({
  leadId,
  isVisible,
}) => {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['disposition-history', leadId, page],
    queryFn: () => api.getDispositionHistory(leadId, { page, limit: 10 }),
    enabled: isVisible && !!leadId,
    staleTime: 30 * 1000,
  });

  const history: any[] = data?.data?.history || [];
  const total: number = data?.data?.total || 0;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className="w-3 h-3 rounded-full bg-gray-200 animate-pulse mt-1" />
              <div className="w-px flex-1 bg-gray-100 mt-1 min-h-8" />
            </div>
            <div className="pb-4 flex-1 space-y-1">
              <div className="bg-gray-200 animate-pulse rounded h-4 w-32" />
              <div className="bg-gray-200 animate-pulse rounded h-3 w-48" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-4 text-center">No call history yet.</p>
    );
  }

  return (
    <div>
      <div className="space-y-0">
        {history.map((entry: any, idx: number) => {
          const dotColor = getDotColor(entry.subDisposition || '');
          const isLast = idx === history.length - 1;
          return (
            <div key={entry.id} className="flex gap-3">
              {/* Left: dot + line */}
              <div className="flex flex-col items-center">
                <div className={`w-3 h-3 rounded-full ${dotColor} mt-1 flex-shrink-0`} />
                {!isLast && <div className="w-px flex-1 bg-gray-200 mt-1 min-h-6" />}
              </div>
              {/* Right: content */}
              <div className={`${isLast ? 'pb-1' : 'pb-4'} flex-1`}>
                <p className="font-medium text-sm text-gray-900">{entry.subDisposition || '—'}</p>
                <p className="text-xs text-gray-500">
                  by {entry.loggedBy?.name || 'Unknown'} &bull; {formatDate(entry.createdAt)} at {formatTime(entry.createdAt)}
                </p>
                {entry.followUpDate && (
                  <p className="text-xs text-blue-600 mt-0.5">
                    Follow-up: {formatDate(entry.followUpDate)}
                  </p>
                )}
                {entry.note && (
                  <p className="text-xs text-gray-500 italic mt-1">"{entry.note}"</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {total > 10 && (
        <button
          onClick={() => setPage(p => p + 1)}
          className="mt-2 text-xs text-blue-600 hover:underline"
        >
          Load more ({total - history.length} remaining)
        </button>
      )}
    </div>
  );
};
